import { Component, computed, DestroyRef, inject, input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { TextField } from './text-field';
import { Select } from './select';
import { DatePickerDisplay } from './date-picker-display';
import { DatePipe } from '@angular/common';
import { LaserEquipment } from './laser-equipment';
import { LaserProcedureType } from './laser-procedure-type';
import { SelectOption } from './select-option';
import { LaserOption } from './laser-option';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LaserTreatmentControlLabels } from './laser-treatment-control-labels';
import { LaserTreatmentControlValue } from './laser-treatment-control-value';
import { LASER_TREATMENT_CONTROL_MASKS } from './laser-treatment-control-masks';
import { TextareaAutosizeDirective } from '../shared/textarea-autosize-directive';
import { PasteStripDirective } from '../shared/paste-strip-directive';
import { LASER_NOTE_MAX_LENGTH } from '../../../models/form-widget-emr-laser.constants';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';
import {
  clearScribeSuggestionMarker,
  copyScribeSuggestionMarker,
} from '../../scribe-suggestion-marker.util';

/**
 * Control for editing a single laser treatment record in the Laser widget.
 */
@Component({
  selector: 'fb-laser-treatment-control',
  imports: [
    ReactiveFormsModule,
    TextField,
    DatePickerDisplay,
    Select,
    SelectOption,
    DatePipe,
    TextareaAutosizeDirective,
    PasteStripDirective,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  templateUrl: './laser-treatment-control.html',
  styleUrl: './laser-treatment-control.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: LaserTreatmentControl, multi: true },
    { provide: NG_VALIDATORS, useExisting: LaserTreatmentControl, multi: true },
  ],
})
export class LaserTreatmentControl implements ControlValueAccessor, Validator, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(NonNullableFormBuilder);

  /**
   * Laser equipment options.
   */
  public readonly equipmentOptions = input.required<readonly LaserEquipment[]>();

  /**
   * Laser procedure types options.
   */
  public readonly procedureTypeOptions = input.required<readonly LaserProcedureType[]>();

  /**
   * Laser options.
   */
  public readonly laserOptions = input.required<readonly LaserOption[]>();

  /**
   * Labels for the laser treatment control.
   */
  public readonly labels = input<LaserTreatmentControlLabels>({
    smartCoolText: null,
    joulesText: null,
    pulseText: null,
    repetitionRateText: null,
    firstLaserOptionText: null,
    secondLaserOptionText: null,
  });

  public readonly markerTarget = input<ScribeSuggestionMarkerTarget | null>(null);

  /**
   * Index of this treatment row in the widget, for the amendment indicator.
   */
  public readonly rowIndex = input<number | null>(null);

  /**
   * Options for first laser.
   */
  public readonly firstLaserOptions = computed(() =>
    this.laserOptions().filter((option) => option.type === 1),
  );

  /**
   * Options for second laser.
   */
  public readonly secondLaserOptions = computed(() =>
    this.laserOptions().filter((option) => option.type === 2),
  );

  /**
   * Masking options for controls.
   */
  protected readonly masks = LASER_TREATMENT_CONTROL_MASKS;

  /**
   * Maximum allowed laser note length.
   */
  protected readonly noteMaxLength = LASER_NOTE_MAX_LENGTH;

  /**
   * Laser treatment form.
   */
  protected readonly form = this.fb.group({
    id: this.fb.control<string | null>(null),
    treatmentId: this.fb.control<string | null>(null),
    date: [new Date()],
    equipment: this.fb.control<LaserEquipment | null>(null),
    procedureType: this.fb.control<LaserProcedureType | null>(null),
    smartCool: [''],
    note: this.fb.control('', { validators: [Validators.maxLength(LASER_NOTE_MAX_LENGTH)] }),
    joules: [''],
    pulse: [''],
    repetitionRate: [''],
    serviceTime: [''],
    firstLaserOption: this.fb.control<LaserOption | null>(null),
    secondLaserOption: this.fb.control<LaserOption | null>(null),
  });

  /**
   * Called when the control is blurred.
   */
  protected onTouched: () => void = () => {};

  /**
   * Called when the control state changes.
   */
  protected onChange: (value: LaserTreatmentControlValue) => void = () => {};

  /**
   * Called when internal validation state changes.
   */
  private onValidatorChange: () => void = () => {};
  private readonly valueMarkerTarget: ScribeSuggestionMarkerTarget = {};

  /**
   * Label for the `Smart Cool` field.
   */
  protected readonly smartCoolLabel = computed(() => {
    const smartCoolText = this.labels().smartCoolText;

    if (smartCoolText === null) {
      return 'Smart Cool';
    }

    return smartCoolText;
  });

  /**
   * Label for the `Joules` field.
   */
  protected readonly joulesLabel = computed(() => {
    const joulesText = this.labels().joulesText;

    if (joulesText === null) {
      return 'Joules';
    }

    return joulesText;
  });

  /**
   * Label for the `Pulse` field.
   */
  protected readonly pulseLabel = computed(() => {
    const pulseText = this.labels().pulseText;

    if (pulseText === null) {
      return 'Pulse';
    }

    return pulseText;
  });

  /**
   * Label for the `Rep Rate` field.
   */
  protected readonly repetitionRateLabel = computed(() => {
    const repetitionRateText = this.labels().repetitionRateText;

    if (repetitionRateText === null) {
      return 'Rep Rate';
    }

    return repetitionRateText;
  });

  /**
   * Label for the `Laser Option 1` field.
   */
  protected readonly firstLaserOptionLabel = computed(() => {
    const firstLaserOptionText = this.labels().firstLaserOptionText;

    if (firstLaserOptionText === null) {
      return 'Laser Option 1';
    }

    return firstLaserOptionText;
  });

  /**
   * Label for the `Laser Option 2` field.
   */
  protected readonly secondLaserOptionLabel = computed(() => {
    const secondLaserOptionText = this.labels().secondLaserOptionText;

    if (secondLaserOptionText === null) {
      return 'Laser Option 2';
    }

    return secondLaserOptionText;
  });

  public ngOnInit(): void {
    this.observeManualFieldChanges();
    this.observeValueChanges();
    this.observeStatusChanges();
  }

  /**
   * Implemented as part of {@link ControlValueAccessor}.
   */
  public writeValue(value: LaserTreatmentControlValue): void {
    copyScribeSuggestionMarker(
      value as unknown as ScribeSuggestionMarkerTarget,
      this.valueMarkerTarget,
    );
    this.form.patchValue(
      {
        id: value.id,
        treatmentId: value.treatmentId,
        date: value.date,
        equipment: value.equipment,
        procedureType: value.procedureType,
        smartCool: this.stringifyNumber(value.smartCool),
        joules: this.stringifyNumber(value.joules),
        repetitionRate: this.stringifyNumber(value.repetitionRate),
        serviceTime: this.stringifyNumber(value.serviceTime),
        pulse: this.stringifyNumber(value.pulse),
        note: value.note,
        firstLaserOption: value.firstLaserOption,
        secondLaserOption: value.secondLaserOption,
      },
      { emitEvent: false },
    );

    this.onValidatorChange();
  }

  /**
   * Implemented as part of {@link ControlValueAccessor}.
   */
  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  /**
   * Implemented as part of {@link ControlValueAccessor}.
   */
  public registerOnChange(onChange: (value: LaserTreatmentControlValue) => void): void {
    this.onChange = onChange;
  }

  /**
   * Implemented as part of {@link Validator}.
   */
  public validate(): ValidationErrors | null {
    return this.form.valid ? null : { laserTreatmentInvalid: true };
  }

  /**
   * Implemented as part of {@link Validator}.
   */
  public registerOnValidatorChange(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }

  /**
   * Compare function for the equipment options.
   */
  protected compareLaserEquipment(a: LaserEquipment, b: LaserEquipment): boolean {
    return a.id === b.id;
  }

  /**
   * Compare function for the laser procedure type options.
   */
  protected compareLaserProcedureType(a: LaserProcedureType, b: LaserProcedureType): boolean {
    return a.id === b.id;
  }

  /**
   * Compare function for the laser options.
   */
  protected compareLaserOption(a: LaserOption, b: LaserOption): boolean {
    return a.id === b.id;
  }

  /**
   * Stringifies a number.
   */
  private stringifyNumber(value: number | null): string {
    if (value === null) {
      return '';
    }

    return value.toString();
  }

  /**
   * Parses a number.
   */
  private parseNumber(value: string | null): number | null {
    if (value === null) {
      return null;
    }

    const parsed = parseFloat(value);

    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Observes value changes and propagates changes to the form control.
   */
  private observeValueChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const value = this.form.getRawValue();

      const result: LaserTreatmentControlValue = {
        id: value.id,
        treatmentId: value.treatmentId,
        date: value.date,
        equipment: value.equipment,
        procedureType: value.procedureType,
        smartCool: this.parseNumber(value.smartCool),
        joules: this.parseNumber(value.joules),
        repetitionRate: this.parseNumber(value.repetitionRate),
        serviceTime: this.parseNumber(value.serviceTime),
        pulse: this.parseNumber(value.pulse),
        note: value.note,
        firstLaserOption: value.firstLaserOption,
        secondLaserOption: value.secondLaserOption,
      };
      copyScribeSuggestionMarker(
        this.valueMarkerTarget,
        result as unknown as ScribeSuggestionMarkerTarget,
      );
      this.onChange(result);
    });
  }

  private observeManualFieldChanges(): void {
    const fields = [
      ['equipment', 'equipment'],
      ['procedureType', 'procedureType'],
      ['serviceTime', 'serviceTime'],
      ['smartCool', 'smartCool'],
      ['joules', 'joules'],
      ['pulse', 'pulse'],
      ['repetitionRate', 'repetitionRate'],
      ['firstLaserOption', 'firstLaserOption'],
      ['secondLaserOption', 'secondLaserOption'],
      ['note', 'note'],
    ] as const;

    fields.forEach(([controlName, markerKey]) => {
      const control = this.form.controls[controlName] as AbstractControl;
      control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.clearManualMarker(markerKey));
    });
  }

  private clearManualMarker(fieldKey: string): void {
    const markerTarget = this.markerTarget();
    if (markerTarget) {
      copyScribeSuggestionMarker(markerTarget, this.valueMarkerTarget);
      clearScribeSuggestionMarker(markerTarget, fieldKey);
    }
    clearScribeSuggestionMarker(this.valueMarkerTarget, fieldKey);
  }

  /**
   * Observes validation changes and propagates them to the parent form control.
   */
  private observeStatusChanges(): void {
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onValidatorChange());
  }
}
